import {
  LocalPathway,
  LocalPathwayScenario,
  InterventionCategory,
  PathwayScenarioWithInventory,
} from '@/types';
import { ReactNode } from 'react';
import { useTheme } from 'styled-components';
import {
  Frame,
  Container,
  Stack,
  InPlaceInput,
  Text,
  Strong,
  Inline,
  InlineItem,
  Anchor,
  Content,
  Divider,
  Button,
  Footer,
  Toolbar,
  ToolbarGroup,
  ToolbarItem,
  Icon,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import { SidebarIntervention } from '@/components/sidebar-intervention';
import { CategoryHeader } from './category-header';

const categories: InterventionCategory[] = [
  'development',
  'energy',
  'transport',
  'waste',
  'ippu',
  'agriculture',
  'forestry',
];

const interventionToCategory = {
  assumption: 'development',
  'assumption-growth': 'development',
  'assumption-percentage': 'development',
  'assumption-derived-dwellings': 'development',
  'grid-decarbonisation': 'energy',
  'grid-decarbonisation-percentage': 'energy',
  'building-efficiency': 'energy',
  'building-electrification': 'energy',
  'new-building-standards': 'energy',
  'mode-shift-to-public-transport': 'transport',
  'mode-shift-to-active-transport': 'transport',
  'electric-vehicle-takeup': 'transport',
  'road-to-rail-freight-mode-shift': 'transport',
  'rail-freight-efficiency': 'transport',
  'air-transport-efficiency': 'transport',
  'waste-reduction': 'waste',
  'waste-diversion-from-landfill': 'waste',
  'waste-treatment': 'waste',
  'wastewater-treatment': 'waste',
  'industrial-processes': 'ippu',
  'product-use': 'ippu',
  livestock: 'agriculture',
  horticulture: 'agriculture',
  'exotic-forest-change': 'forestry',
};

const PathwayScenarioSidebar = (props: PathwayScenarioSidebarProps) => {
  const {
    detailPath,
    location,
    configuration,
    scenario,
    isDirty = false,
    onRename = noop,
    onSave = noop,
    onDiscard = noop,
    onUpdateIntervention = noop,
    enableCopy = false,
    scenarios = [],
    onCopyIntervention,
  } = props;
  const theme = useTheme() as any;
  return (
    <Container>
      <Frame sizing='fill-container'>
        <Content padding='medium'>
          <Stack space='medium'>
            <InPlaceInput
              variant='title'
              value={scenario.name}
              onChange={onRename}
            />
            <Text>
              Explore alternative emission reduction pathways on your way to
              decarbonisation. Adjust inputs for the intervention categories
              below and instantly review your decarbonisation results on the
              right.{' '}
              <Anchor
                target='_blank'
                href='https://help.kinesis.org/en/collections/13434139-community-decarbonisation'
              >
                Learn more about this model.
              </Anchor>
            </Text>
            <Divider />
            {/* The configuration rows (the new pathway's scenario / base
                end-month / place editors) when the surface has them;
                otherwise the legacy static location row. */}
            {configuration ?? (
              <Inline space='small'>
                <InlineItem sizing='fit-content'>
                  <Icon type='map-filled' color='rgba(0,0,0,0.45)' />
                </InlineItem>
                <InlineItem sizing='fill-container'>
                  <Strong>{location}</Strong>
                </InlineItem>
              </Inline>
            )}
            <Divider />

            {categories.map((category) => (
              <>
                <CategoryHeader category={category} detailPath={detailPath} />
                {scenario.interventions
                  .filter(
                    (v: any) =>
                      (interventionToCategory as any)[v.type] === category,
                  )
                  .map((i: any) => (
                    <SidebarIntervention
                      intervention={i}
                      detailPath={detailPath}
                      onUpdateIntervention={onUpdateIntervention}
                      enableCopy={enableCopy}
                      scenarios={scenarios}
                      onCopyIntervention={onCopyIntervention}
                    />
                  ))}
              </>
            ))}
          </Stack>
        </Content>
      </Frame>
      {isDirty && (
        <Footer border>
          <Toolbar>
            <ToolbarGroup>
              <ToolbarItem>
                <Icon type='info-circle' color={theme.color.yellow6} />
              </ToolbarItem>
              <ToolbarItem ellipsify>
                <Text>Save your changes to make them visible to others.</Text>
              </ToolbarItem>
            </ToolbarGroup>
            <ToolbarGroup>
              <ToolbarItem>
                <Button variant='secondary' onClick={onDiscard}>
                  Discard
                </Button>
              </ToolbarItem>
              <ToolbarItem>
                <Button variant='primary' onClick={onSave}>
                  Save
                </Button>
              </ToolbarItem>
            </ToolbarGroup>
          </Toolbar>
        </Footer>
      )}
    </Container>
  );
};

export type PathwayScenarioSidebarProps = {
  // The absolute path of the owning detail page — the intervention and
  // category links hang off it (legacy: `pathwayScenarioPath(...)`; the
  // new pathway page: `pathwayPath(...)`).
  detailPath: string;
  // The legacy static location row; ignored when `configuration` renders
  // in its place.
  location?: string;
  configuration?: ReactNode;
  scenario: LocalPathwayScenario | LocalPathway;
  isDirty?: boolean;
  onUpdateIntervention?: (id: string | number, v: (x: any) => any) => void;
  onRename?: (v: String) => void;
  onDiscard?: () => void;
  onSave?: () => void;
  enableCopy?: boolean;
  scenarios?: PathwayScenarioWithInventory[];
  onCopyIntervention?: (scenarioId: number, intervention: any) => Promise<void>;
};

export { PathwayScenarioSidebar };
